import React, { useEffect, useState } from 'react';
import './PremiumPlanInfoTooltip.less';
import { CloseIcon, QuestionIcon } from 'app/components/icons';
import { IconButton } from 'app/components/buttons';
import { useIsMobile } from 'app/hooks/useIsMobile';
import { useTranslation } from 'react-i18next';
import {
  offset,
  autoUpdate,
  useFloating,
  useTransitionStyles,
  flip,
  shift,
} from '@floating-ui/react';
import ClickOutside from 'app/widgets/ClickOutside';
import { horizontalCenterUnderReference } from './utils';

export default function PremiumPlanInfoTooltip() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  const { refs, floatingStyles, update, context } = useFloating({
    placement: isMobile ? 'bottom' : 'right',
    strategy: 'absolute',
    middleware: isMobile
      ? [horizontalCenterUnderReference()]
      : [
          offset({ mainAxis: 8 }),
          flip({
            boundary: 'clippingAncestors',
            padding: 10,
          }),
          shift({
            boundary: 'clippingAncestors',
            padding: 10,
          }),
        ],
    whileElementsMounted: autoUpdate,
    open,
  });

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context);

  useEffect(() => {
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);

  return (
    <>
      <IconButton
        unstyled
        ref={refs.setReference}
        onClick={() => setOpen(value => !value)}
      >
        <QuestionIcon className="premium-plan-info-tooltip__anchor" />
      </IconButton>

      {isMounted && (
        <ClickOutside
          onClickOutside={e => {
            if (e.target !== refs.reference.current) {
              setOpen(false);
            }
          }}
        >
          <div
            ref={refs.setFloating}
            className="premium-plan-info-tooltip"
            style={{
              ...floatingStyles,
              ...transitionStyles,
              zIndex: 1000000,
            }}
          >
            <div className="premium-plan-info-tooltip__title-row">
              <h3 className="premium-plan-info-tooltip__title">
                {t(
                  'subscriptions.manage_your_plan.premium_telematics_plan.title',
                )}
              </h3>
              <IconButton unstyled onClick={() => setOpen(false)}>
                <CloseIcon height={18} width={18} stroke="#909090" />
              </IconButton>
            </div>

            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.unlimited_updates',
                ),
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.current_machine_hours_and_status',
                ),
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.current_machine_vitals',
                ),
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.current_machine_codes',
                ),
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.accurate_location',
                ),
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.inhibit_restart',
                ),
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.aemp',
                ),
              }}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: t(
                  'subscriptions.manage_your_plan.benefits.tooltip.enhanced_notifications',
                ),
              }}
            />
          </div>
        </ClickOutside>
      )}
    </>
  );
}
